if (!customElements.get('cdi-selectx')) {
  class CdiSelectX extends HTMLElement {
    connectedCallback() {
      if (this._built) return;
      this._native = this.querySelector('select');
      if (!this._native) return;
      this._sortNavigate = this.hasAttribute('data-sort-select');
      this._build();
      this._bindOutsideClick();
      this._built = true;
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onOutside);
      document.removeEventListener('focusin', this._onOutside);
    }

    _build() {
      const native = this._native;

      const shell = this.querySelector('[data-cdi-shell]');
      if (shell) {
        this._trigger = shell;
        this._valueEl = shell.querySelector('.cdi-selectx__value');
      } else {
        this._trigger = document.createElement('button');
        this._trigger.type = 'button';
        this._trigger.className = 'cdi-selectx__trigger';
        this._trigger.setAttribute('aria-haspopup', 'listbox');
        this._trigger.setAttribute('aria-expanded', 'false');

        this._valueEl = document.createElement('span');
        this._valueEl.className = 'cdi-selectx__value';

        const caret = document.createElement('span');
        caret.className = 'cdi-selectx__caret svg-wrapper';
        caret.setAttribute('aria-hidden', 'true');
        caret.innerHTML = this._caretSvg();

        this._trigger.appendChild(this._valueEl);
        this._trigger.appendChild(caret);
        this.appendChild(this._trigger);
      }

      this._dropdown = document.createElement('ul');
      this._dropdown.className = 'cdi-selectx__dropdown';
      this._dropdown.setAttribute('role', 'listbox');

      this._renderOptions();
      this._syncValue();

      this.appendChild(this._dropdown);

      this._trigger.addEventListener('click', () => this._isOpen() ? this._close() : this._open());
      this._trigger.addEventListener('keydown', (e) => this._onTriggerKey(e));
      this._dropdown.addEventListener('click', (e) => {
        const opt = e.target.closest('.cdi-selectx__option');
        if (opt && !opt.dataset.disabled) this._select(opt.dataset.value);
      });
      this._dropdown.addEventListener('keydown', (e) => this._onDropdownKey(e));

      native.addEventListener('change', () => this._syncValue());
    }

    refreshOptions() {
      if (!this._dropdown || !this._native) return;
      this._renderOptions();
      this._syncValue();
    }

    _renderOptions() {
      this._dropdown.innerHTML = '';
      Array.from(this._native.options).forEach((opt) => {
        const li = document.createElement('li');
        li.className = 'cdi-selectx__option';
        li.setAttribute('role', 'option');
        li.setAttribute('tabindex', '0');
        li.dataset.value = opt.value;
        li.textContent = opt.text;
        if (opt.selected) li.classList.add('cdi-selectx__option--selected');
        this._dropdown.appendChild(li);
      });
    }

    _syncValue() {
      const selected = this._native.options[this._native.selectedIndex];
      this._valueEl.textContent = selected ? selected.text : '';
      this._dropdown.querySelectorAll('.cdi-selectx__option').forEach((li) => {
        li.classList.toggle('cdi-selectx__option--selected', li.dataset.value === this._native.value);
        li.setAttribute('aria-selected', li.dataset.value === this._native.value ? 'true' : 'false');
      });
    }

    _select(value) {
      if (this._sortNavigate) {
        var url = new URL(window.location.href);
        if (value) {
          url.searchParams.set('sort_by', value);
        } else {
          url.searchParams.delete('sort_by');
        }
        window.location.href = url.toString();
        return;
      }

      this._native.value = value;
      this._native.dispatchEvent(new Event('change', { bubbles: true }));
      this._syncValue();
      this._close();
      this._trigger.focus();
    }

    _open() {
      this.setAttribute('open', '');
      this._trigger.setAttribute('aria-expanded', 'true');
      const selected = this._dropdown.querySelector('.cdi-selectx__option--selected')
        || this._dropdown.querySelector('.cdi-selectx__option');
      if (selected) {
        selected.classList.add('cdi-selectx__option--focused');
        if (window.matchMedia('(hover: hover)').matches) {
          selected.focus({ preventScroll: true });
        }
      }
    }

    _close() {
      this.removeAttribute('open');
      this._trigger.setAttribute('aria-expanded', 'false');
      this._dropdown.querySelectorAll('.cdi-selectx__option--focused')
        .forEach(el => el.classList.remove('cdi-selectx__option--focused'));
      if (!window.matchMedia('(hover: hover)').matches) {
        this._trigger.blur();
      }
    }

    _isOpen() { return this.hasAttribute('open'); }

    _onTriggerKey(e) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this._open();
      } else if (e.key === 'Escape') {
        this._close();
      }
    }

    _onDropdownKey(e) {
      const opts = Array.from(this._dropdown.querySelectorAll('.cdi-selectx__option'));
      const focused = this._dropdown.querySelector('.cdi-selectx__option--focused');
      const idx = opts.indexOf(focused);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = opts[idx + 1] || opts[0];
        this._focusOption(next);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = opts[idx - 1] || opts[opts.length - 1];
        this._focusOption(prev);
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (focused) this._select(focused.dataset.value);
      } else if (e.key === 'Escape' || e.key === 'Tab') {
        this._close();
        this._trigger.focus();
      }
    }

    _focusOption(el) {
      this._dropdown.querySelectorAll('.cdi-selectx__option--focused')
        .forEach(o => o.classList.remove('cdi-selectx__option--focused'));
      el.classList.add('cdi-selectx__option--focused');
      el.scrollIntoView({ block: 'nearest' });
      el.focus({ preventScroll: true });
    }

    _bindOutsideClick() {
      this._onOutside = (e) => {
        if (this._isOpen() && !this.contains(e.target)) this._close();
      };
      document.addEventListener('click', this._onOutside);
      document.addEventListener('focusin', this._onOutside);
    }

    _caretSvg() {
      return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>';
    }
  }

  customElements.define('cdi-selectx', CdiSelectX);
}
